<template>
  <div id="guava-vs-govava-view">
    <section class="bg-light">
      <main class="disambiguation-page">
        <div class="container py-5">
          <h1 class="main-title">Guava vs Govava</h1>
          <p class="lead">
            Searching for &ldquo;guava&rdquo; and landed here? You&rsquo;re in the
            right place &mdash; Govava is an AI-powered gift finder, not the
            tropical fruit.
          </p>

          <div class="comparison-grid">
            <div class="comparison-card">
              <h2>Guava (the fruit)</h2>
              <p>
                A tropical fruit with pink or white flesh, commonly eaten fresh
                or used in juices, jams, and desserts.
              </p>
            </div>
            <div class="comparison-card highlight">
              <h2>Govava (this website)</h2>
              <p>
                An AI gift concierge that recommends personalized gifts from
                3,000+ retail stores via Rakuten based on the recipient&rsquo;s
                personality, interests, and the occasion.
              </p>
            </div>
          </div>

          <p class="title">What is Govava?</p>
          <p>
            Govava aggregates retailers including Amazon, Etsy, Walmart, and
            Rakuten-network stores, then uses AI &mdash; persona modeling,
            occasion detection, and gift-matching &mdash; to recommend tailored
            gifts. Govava is free to use and earns revenue through affiliate
            commissions when you complete a purchase on a retailer&rsquo;s site.
          </p>

          <p class="title">Frequently asked questions</p>
          <ul class="faq-list">
            <li v-for="(faq, index) in faqs" :key="index" class="faq-item">
              <h3>{{ faq.question }}</h3>
              <p>{{ faq.answer }}</p>
            </li>
          </ul>

          <div class="cta-block">
            <p>Ready to find the perfect gift?</p>
            <router-link to="/" class="btn btn-primary btn-lg">
              Try the AI Gift Wizard
            </router-link>
          </div>
        </div>
      </main>
    </section>
  </div>
</template>

<script>
const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is Govava related to guava the fruit?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "No. Govava is an AI-powered gift discovery platform, unrelated to the tropical fruit guava. The similarity in names is coincidental. If you're looking for gift ideas, Govava can help you find the perfect present using AI-matched recommendations.",
      },
    },
    {
      "@type": "Question",
      name: "What is Govava?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Govava is an AI gift concierge that recommends personalized gifts from 3,000+ retail stores via Rakuten, plus Amazon, Etsy, and Walmart, based on the recipient's personality, interests, and the occasion.",
      },
    },
    {
      "@type": "Question",
      name: "How is Govava different from a regular gift guide?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Unlike generic gift guides that show the same items to every visitor, Govava uses AI to build a profile of the specific person you are shopping for and tailors recommendations accordingly.",
      },
    },
    {
      "@type": "Question",
      name: "Where can I use Govava?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Govava is available at govava.com and through the Govava mobile app on iOS and Android.",
      },
    },
  ],
};

export default {
  name: "GuavaVsGovavaView",
  metaInfo: {
    title: "Guava vs Govava | AI Gift Finder (Not the Fruit)",
    meta: [
      {
        name: "description",
        content:
          "Govava is an AI-powered gift discovery platform — not the tropical fruit guava. Learn the difference and find personalized gift ideas in seconds.",
      },
      {
        name: "keywords",
        content:
          "guava vs govava, govava gift finder, AI gift wizard, guava fruit confusion, personalized gifts, gift recommendations",
      },
      {
        property: "og:title",
        content: "Guava vs Govava | AI Gift Finder (Not the Fruit)",
      },
      {
        property: "og:description",
        content:
          "Govava is an AI-powered gift discovery platform — not the tropical fruit guava. Learn the difference and find personalized gift ideas.",
      },
      {
        property: "og:url",
        content: "https://www.govava.com/guava-vs-govava",
      },
    ],
    script: [
      {
        type: "application/ld+json",
        json: FAQ_SCHEMA,
      },
    ],
  },
  data() {
    return {
      faqs: FAQ_SCHEMA.mainEntity.map((item) => ({
        question: item.name,
        answer: item.acceptedAnswer.text,
      })),
    };
  },
};
</script>

<style scoped>
.disambiguation-page {
  background: #ffffff;
}

.main-title {
  color: #2c3e50;
  text-align: center;
  font-weight: 500;
  font-size: 32px;
  margin-bottom: 1rem;
}

.lead {
  text-align: center;
  font-size: 18px;
  color: #555;
  max-width: 720px;
  margin: 0 auto 2.5rem;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

@media (max-width: 768px) {
  .comparison-grid {
    grid-template-columns: 1fr;
  }
}

.comparison-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  background: #fafafa;
}

.comparison-card.highlight {
  border-color: #599ce7;
  background: #f0f6fd;
}

.comparison-card h2 {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 0.75rem;
}

.title {
  color: #2c3e50;
  font-size: 20px;
  font-weight: 500;
  margin-top: 1.5rem;
}

.faq-list {
  list-style: none;
  padding: 0;
  margin: 1rem 0 2rem;
}

.faq-item {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #eee;
}

.faq-item h3 {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.faq-item p {
  color: #555;
  margin: 0;
}

.cta-block {
  text-align: center;
  padding: 2rem 0 1rem;
}

.cta-block p {
  font-size: 18px;
  margin-bottom: 1rem;
  color: #2c3e50;
}
</style>
